export const TRACKING_SCHEMA_VERSION = "v1";

const TRACKING_ROUTE_PATTERNS = [
  { section: "smrt_search", pattern: /^\/smrt-search(\/|$)/ },
  { section: "compare", pattern: /^\/compare(\/|$)/ },
  { section: "favorites", pattern: /^\/favorites(\/|$)/ },
  { section: "shadow_team", pattern: /^\/shadow-team(\/|$)/ },
  { section: "playlist", pattern: /^\/my-playlists(\/|$)/ },
  { section: "shared_playlist", pattern: /^\/shared_playlist(\/|$)/ },
  { section: "video", pattern: /^\/tournaments\/match\/video(s)?(\/|$)/ },
];

const EXCLUDED_ROUTE_PATTERNS = [
  /^\/$/,
  /^\/login(\/|$)/,
  /^\/register(\/|$)/,
  /^\/forgot-password(\/|$)/,
  /^\/password-reset(\/|$)/,
  /^\/success(\/|$)/,
  /^\/terms(\/|$)/,
  /^\/privacy(\/|$)/,
  /^\/error(\/|$)/,
  /^\/contact-us(\/|$)/,
  /^\/my-account(\/|$)/,
  /^\/platform-setup(\/|$)/,
  /^\/setup(\/|$)/,
];

const ALLOWED_QUERY_KEYS = new Set([
  "page",
  "sort",
  "search",
  "statMode",
  "seasonId",
  "video",
  "selectedCompetition",
  "selectedSeason",
  "selectedNationality",
  "selectedPosition",
  "selectedLeg",
  "selectedGender",
  "selectedTeamType",
]);

const DENIED_QUERY_KEYS = [
  "token",
  "access_token",
  "refresh_token",
  "auth",
  "authorization",
  "code",
  "email",
  "password",
  "secret",
  "session",
];

const MAX_METADATA_DEPTH = 4;
const MAX_ARRAY_ITEMS = 50;
const MAX_STRING_LENGTH = 500;

const getNavigator = () => (typeof navigator !== "undefined" ? navigator : null);
const getWindow = () => (typeof window !== "undefined" ? window : null);

const parseBrowserName = (userAgent = "") => {
  const ua = String(userAgent || "");
  if (/Edg\//.test(ua)) return "Edge";
  if (/OPR\//.test(ua) || /Opera/.test(ua)) return "Opera";
  if (/Firefox\//.test(ua)) return "Firefox";
  if (/CriOS\//.test(ua)) return "Chrome";
  if (/Chrome\//.test(ua) && !/Chromium\//.test(ua)) return "Chrome";
  if (/Safari\//.test(ua) && /Version\//.test(ua)) return "Safari";
  return null;
};

const parseOsName = (userAgent = "") => {
  const ua = String(userAgent || "");
  if (/Windows NT/i.test(ua)) return "Windows";
  if (/Android/i.test(ua)) return "Android";
  if (/(iPhone|iPad|iPod)/i.test(ua)) return "iOS";
  if (/Mac OS X/i.test(ua)) return "macOS";
  if (/Linux/i.test(ua)) return "Linux";
  return null;
};

const getDeviceType = ({ maxTouchPoints = 0, userAgent = "", viewportWidth = 0 } = {}) => {
  const ua = String(userAgent || "");
  if (/iPad|Tablet|PlayBook|Silk/i.test(ua)) return "tablet";
  if (/Mobi|Android|iPhone|iPod|IEMobile|BlackBerry/i.test(ua)) {
    return /Android/i.test(ua) && viewportWidth >= 768 ? "tablet" : "mobile";
  }
  if (maxTouchPoints > 1 && viewportWidth >= 768 && viewportWidth <= 1366) {
    return "tablet";
  }
  if (viewportWidth > 0 && viewportWidth < 768) {
    return "mobile";
  }
  return "desktop";
};

export const generateTrackingId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  const randomPart = Math.random().toString(36).slice(2, 10);
  return `${Date.now()}-${randomPart}`;
};

export const ensureDeviceId = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const existingDeviceId = localStorage.getItem("device_id");

    if (existingDeviceId) {
      return existingDeviceId;
    }

    const nextDeviceId = generateTrackingId();
    localStorage.setItem("device_id", nextDeviceId);
    return nextDeviceId;
  } catch (error) {
    return null;
  }
};

export const getDeviceContext = () => {
  const browserWindow = getWindow();
  const browserNavigator = getNavigator();
  const userAgent = browserNavigator?.userAgent || "";
  const maxTouchPoints = Number(browserNavigator?.maxTouchPoints || 0);
  const viewportWidth = Number(browserWindow?.innerWidth || 0);

  let timezone = null;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch (error) {
    timezone = null;
  }

  return {
    type: getDeviceType({ maxTouchPoints, userAgent, viewportWidth }),
    browser: parseBrowserName(userAgent),
    os: parseOsName(userAgent),
    screen_width: Number(browserWindow?.screen?.width || 0) || null,
    screen_height: Number(browserWindow?.screen?.height || 0) || null,
    viewport_width: viewportWidth || null,
    viewport_height: Number(browserWindow?.innerHeight || 0) || null,
    pixel_ratio: Number(browserWindow?.devicePixelRatio || 1),
    touch:
      maxTouchPoints > 0 ||
      Boolean(browserWindow && "ontouchstart" in browserWindow),
    language: browserNavigator?.language || null,
    timezone,
    user_agent: userAgent || null,
  };
};

export const getSourceSectionFromPath = (pathname = "") => {
  const matched = TRACKING_ROUTE_PATTERNS.find(({ pattern }) => pattern.test(pathname));
  return matched ? matched.section : null;
};

export const isTrackableBusinessPath = (pathname = "") => {
  if (!pathname) {
    return false;
  }

  if (EXCLUDED_ROUTE_PATTERNS.some((pattern) => pattern.test(pathname))) {
    return false;
  }

  return Boolean(getSourceSectionFromPath(pathname));
};

const isAllowedQueryKey = (key = "") => {
  const normalizedKey = String(key || "").toLowerCase();

  if (!normalizedKey) {
    return false;
  }

  if (DENIED_QUERY_KEYS.some((deniedKey) => normalizedKey.includes(deniedKey))) {
    return false;
  }

  if (ALLOWED_QUERY_KEYS.has(key)) {
    return true;
  }

  if (/^selected[A-Z]/.test(key)) {
    return true;
  }

  if (/^param_\d+_(from|to|mode)$/.test(key)) {
    return true;
  }

  return false;
};

export const sanitizeQueryParams = (search = "") => {
  if (!search) {
    return {};
  }

  try {
    const params = new URLSearchParams(search);
    const sanitizedEntries = [];

    params.forEach((value, key) => {
      if (!isAllowedQueryKey(key)) {
        return;
      }

      sanitizedEntries.push([key, value]);
    });

    return sanitizedEntries.reduce((accumulator, [key, value]) => {
      accumulator[key] = value;
      return accumulator;
    }, {});
  } catch (error) {
    return {};
  }
};

export const buildSanitizedRoute = (pathname = "", search = "") => {
  const sanitizedQuery = sanitizeQueryParams(search);
  const sanitizedSearch = new URLSearchParams(sanitizedQuery).toString();

  return sanitizedSearch ? `${pathname}?${sanitizedSearch}` : pathname;
};

export const sanitizeTrackingValue = (value, depth = 0) => {
  if (depth > MAX_METADATA_DEPTH) {
    return undefined;
  }

  if (value === null) {
    return null;
  }

  if (value === undefined) {
    return undefined;
  }

  if (typeof value === "string") {
    return value.length > MAX_STRING_LENGTH
      ? value.slice(0, MAX_STRING_LENGTH)
      : value;
  }

  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      return undefined;
    }

    return Number(value.toFixed(3));
  }

  if (typeof value === "boolean") {
    return value;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (Array.isArray(value)) {
    const sanitizedArray = value
      .slice(0, MAX_ARRAY_ITEMS)
      .map((item) => sanitizeTrackingValue(item, depth + 1))
      .filter((item) => item !== undefined);

    return sanitizedArray;
  }

  if (typeof value === "object") {
    const sanitizedObject = Object.entries(value).reduce((accumulator, [key, item]) => {
      const sanitizedItem = sanitizeTrackingValue(item, depth + 1);

      if (sanitizedItem !== undefined) {
        accumulator[key] = sanitizedItem;
      }

      return accumulator;
    }, {});

    return sanitizedObject;
  }

  return undefined;
};

export const compactTrackingObject = (value) => sanitizeTrackingValue(value) || {};
